import React, { useState, useMemo, useCallback } from 'react';
import {
  StyleSheet,
  Pressable,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { XStack, YStack } from '@tamagui/stacks';
import { Sheet } from '@tamagui/sheet';
import { Text } from '@tamagui/core';

import { COLORS } from '@/constants/colors';
import { CustomButton } from '@/components';
import { useGenders } from '@/api/hooks/useGendersAndHobbies';
import type { MeetingAnnouncementSearchParams } from '@/api/types';

type FilterSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isDark?: boolean;
  onApplyFilters?: (params: MeetingAnnouncementSearchParams) => void;
};

type Category = {
  id: string;
  label: string;
};

type WhenOption = 'today' | 'tomorrow' | 'week' | 'any';

// Hardcoded categories (API doesn't provide meeting types endpoint)
const CATEGORIES: Category[] = [
  { id: '1', label: 'Kawa' },
  { id: '2', label: 'Spacer' },
  { id: '3', label: 'Muzeum' },
  { id: '4', label: 'Kolacja' },
  { id: '5', label: 'Zakupy' },
];

/**
 * Calculate date range based on when option
 */
const getDateRange = (
  option: WhenOption,
): { dateFrom?: string; dateTo?: string } => {
  const today = new Date();
  const formatDate = (d: Date) => d.toISOString().split('T')[0];

  switch (option) {
    case 'today':
      return { date_from: formatDate(today), date_to: formatDate(today) };
    case 'tomorrow': {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return { date_from: formatDate(tomorrow), date_to: formatDate(tomorrow) };
    }
    case 'week': {
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);
      return { date_from: formatDate(today), date_to: formatDate(weekEnd) };
    }
    case 'any':
    default:
      return {};
  }
};

const FilterSheet = React.memo<FilterSheetProps>(
  ({ open, onOpenChange, isDark, onApplyFilters }) => {
    const [maxDistance, setMaxDistance] = useState(5);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [priceRange, setPriceRange] = useState([0, 100]);
    const [whenOption, setWhenOption] = useState<WhenOption>('any');
    const [selectedGenderIds, setSelectedGenderIds] = useState<number[]>([]);

    // Fetch genders from API
    const { data: gendersData = [], isLoading: isLoadingGenders } =
      useGenders();

    // Ensure genders is always an array
    const genders = Array.isArray(gendersData) ? gendersData : [];

    const bgColor = isDark ? COLORS.BLACK : COLORS.WHITE;
    const textColor = isDark ? COLORS.WHITE : COLORS.DARK_TEXT;
    const subtextColor = isDark ? COLORS.GRAY_400 : COLORS.GRAY_500;

    const toggleCategory = useCallback((categoryId: string) => {
      setSelectedCategories(prev =>
        prev.includes(categoryId)
          ? prev.filter(id => id !== categoryId)
          : [...prev, categoryId],
      );
    }, []);

    const toggleGender = useCallback((genderId: number) => {
      setSelectedGenderIds(prev =>
        prev.includes(genderId)
          ? prev.filter(id => id !== genderId)
          : [...prev, genderId],
      );
    }, []);

    const handleApplyFilters = useCallback(() => {
      const dateRange = getDateRange(whenOption);

      const params: MeetingAnnouncementSearchParams = {
        ...dateRange,
        payment_min: priceRange[0] > 0 ? priceRange[0] : undefined,
        payment_max: priceRange[1] < 200 ? priceRange[1] : undefined,
        gender_ids:
          selectedGenderIds.length > 0 ? selectedGenderIds : undefined,
        // Note: categories/meetingTypeId filtering would need backend support
      };

      // Remove undefined values
      Object.keys(params).forEach(key => {
        if (
          params[key as keyof MeetingAnnouncementSearchParams] === undefined
        ) {
          delete params[key as keyof MeetingAnnouncementSearchParams];
        }
      });

      onApplyFilters?.(params);
      onOpenChange(false);
    }, [
      whenOption,
      priceRange,
      selectedGenderIds,
      onApplyFilters,
      onOpenChange,
    ]);

    const handleClear = useCallback(() => {
      setMaxDistance(5);
      setSelectedCategories([]);
      setPriceRange([0, 100]);
      setWhenOption('any');
      setSelectedGenderIds([]);
      onApplyFilters?.({});
      onOpenChange(false);
    }, [onApplyFilters, onOpenChange]);

    // Placeholder count - would need API endpoint to get actual count
    const announcementCount = 47;

    return (
      <Sheet
        modal
        open={open}
        onOpenChange={onOpenChange}
        snapPoints={[85]}
        dismissOnSnapToBottom
      >
        <Sheet.Overlay backgroundColor="rgba(0, 0, 0, 0.5)" />
        <Sheet.Frame
          backgroundColor={bgColor}
          borderTopLeftRadius={24}
          borderTopRightRadius={24}
        >
          <Sheet.Handle backgroundColor={subtextColor} />

          {/* Header */}
          <XStack
            justifyContent="space-between"
            alignItems="center"
            paddingHorizontal="$4"
            paddingVertical="$3"
            borderBottomWidth={1}
            borderBottomColor={
              isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
            }
          >
            <Pressable onPress={() => onOpenChange(false)}>
              <Text fontSize={16} color={textColor}>
                ← Filtry
              </Text>
            </Pressable>
            <Pressable onPress={handleClear}>
              <Text fontSize={14} color={COLORS.PRIMARY}>
                Wyczyść
              </Text>
            </Pressable>
          </XStack>

          <ScrollView style={styles.scrollView}>
            <YStack padding="$4" gap="$5" paddingBottom="$8">
              {/* Maximum Distance */}
              <YStack gap="$2">
                <XStack justifyContent="space-between" alignItems="center">
                  <Text fontSize={16} fontWeight="600" color={textColor}>
                    Maksymalny dystans
                  </Text>
                  <Text fontSize={16} fontWeight="600" color={textColor}>
                    {maxDistance}km
                  </Text>
                </XStack>
                {/* <Slider
                style={styles.slider}
                minimumValue={1}
                maximumValue={50}
                step={1}
                value={maxDistance}
                onValueChange={setMaxDistance}
                minimumTrackTintColor={COLORS.PRIMARY}
                maximumTrackTintColor={isDark ? COLORS.GRAY_400 : COLORS.GRAY_300}
                thumbTintColor={COLORS.PRIMARY}
              /> */}
              </YStack>

              {/* Categories */}
              <YStack gap="$3">
                <Text fontSize={16} fontWeight="600" color={textColor}>
                  Kategorie
                </Text>
                <XStack flexWrap="wrap" gap="$2">
                  {CATEGORIES.map(category => (
                    <Pressable
                      key={category.id}
                      onPress={() => toggleCategory(category.id)}
                    >
                      <YStack
                        paddingHorizontal="$4"
                        paddingVertical="$2.5"
                        borderRadius={20}
                        borderWidth={1}
                        borderColor={
                          selectedCategories.includes(category.id)
                            ? COLORS.PRIMARY
                            : isDark
                            ? 'rgba(255, 255, 255, 0.2)'
                            : 'rgba(0, 0, 0, 0.2)'
                        }
                        backgroundColor={
                          selectedCategories.includes(category.id)
                            ? COLORS.PRIMARY
                            : 'transparent'
                        }
                      >
                        <Text
                          fontSize={14}
                          fontWeight="500"
                          color={
                            selectedCategories.includes(category.id)
                              ? COLORS.WHITE
                              : textColor
                          }
                        >
                          {category.label}
                        </Text>
                      </YStack>
                    </Pressable>
                  ))}
                </XStack>
              </YStack>

              {/* Gender Filter */}
              <YStack gap="$3">
                <Text fontSize={16} fontWeight="600" color={textColor}>
                  Płeć
                </Text>
                {isLoadingGenders ? (
                  <ActivityIndicator size="small" color={COLORS.PRIMARY} />
                ) : Array.isArray(genders) && genders.length > 0 ? (
                  <XStack flexWrap="wrap" gap="$2">
                    {genders.map(gender => (
                      <Pressable
                        key={gender.id}
                        onPress={() => toggleGender(gender.id)}
                      >
                        <YStack
                          paddingHorizontal="$4"
                          paddingVertical="$2.5"
                          borderRadius={20}
                          borderWidth={1}
                          borderColor={
                            selectedGenderIds.includes(gender.id)
                              ? COLORS.PRIMARY
                              : isDark
                              ? 'rgba(255, 255, 255, 0.2)'
                              : 'rgba(0, 0, 0, 0.2)'
                          }
                          backgroundColor={
                            selectedGenderIds.includes(gender.id)
                              ? COLORS.PRIMARY
                              : 'transparent'
                          }
                        >
                          <Text
                            fontSize={14}
                            fontWeight="500"
                            color={
                              selectedGenderIds.includes(gender.id)
                                ? COLORS.WHITE
                                : textColor
                            }
                          >
                            {gender.name || `Płeć ${gender.id}`}
                          </Text>
                        </YStack>
                      </Pressable>
                    ))}
                  </XStack>
                ) : (
                  <Text fontSize={14} color={subtextColor}>
                    Brak dostępnych płci
                  </Text>
                )}
              </YStack>

              {/* Price Range */}
              <YStack gap="$2">
                <XStack justifyContent="space-between" alignItems="center">
                  <Text fontSize={16} fontWeight="600" color={textColor}>
                    Zakres cenowy
                  </Text>
                  <Text fontSize={16} fontWeight="600" color={textColor}>
                    {priceRange[0]}-{priceRange[1]}zł
                  </Text>
                </XStack>
                {/* <Slider
                style={styles.slider}
                minimumValue={0}
                maximumValue={200}
                step={5}
                value={priceRange[1]}
                onValueChange={(val) => setPriceRange([priceRange[0], val])}
                minimumTrackTintColor={COLORS.PRIMARY}
                maximumTrackTintColor={isDark ? COLORS.GRAY_400 : COLORS.GRAY_300}
                thumbTintColor={COLORS.PRIMARY}
              /> */}
              </YStack>

              <YStack gap="$3">
                <Text fontSize={16} fontWeight="600" color={textColor}>
                  Kiedy
                </Text>
                <XStack flexWrap="wrap" gap="$2">
                  <Pressable
                    onPress={() => setWhenOption('today')}
                    style={{ flex: 1, minWidth: '45%' }}
                  >
                    <YStack
                      paddingVertical="$3"
                      borderRadius={12}
                      borderWidth={1}
                      borderColor={
                        whenOption === 'today'
                          ? COLORS.PRIMARY
                          : isDark
                          ? 'rgba(255, 255, 255, 0.2)'
                          : 'rgba(0, 0, 0, 0.2)'
                      }
                      backgroundColor={
                        whenOption === 'today' ? COLORS.PRIMARY : 'transparent'
                      }
                      alignItems="center"
                    >
                      <Text
                        fontSize={14}
                        fontWeight="500"
                        color={
                          whenOption === 'today' ? COLORS.WHITE : textColor
                        }
                      >
                        Dziś
                      </Text>
                    </YStack>
                  </Pressable>

                  <Pressable
                    onPress={() => setWhenOption('tomorrow')}
                    style={{ flex: 1, minWidth: '45%' }}
                  >
                    <YStack
                      paddingVertical="$3"
                      borderRadius={12}
                      borderWidth={1}
                      borderColor={
                        whenOption === 'tomorrow'
                          ? COLORS.PRIMARY
                          : isDark
                          ? 'rgba(255, 255, 255, 0.2)'
                          : 'rgba(0, 0, 0, 0.2)'
                      }
                      backgroundColor={
                        whenOption === 'tomorrow'
                          ? COLORS.PRIMARY
                          : 'transparent'
                      }
                      alignItems="center"
                    >
                      <Text
                        fontSize={14}
                        fontWeight="500"
                        color={
                          whenOption === 'tomorrow' ? COLORS.WHITE : textColor
                        }
                      >
                        Jutro
                      </Text>
                    </YStack>
                  </Pressable>

                  <Pressable
                    onPress={() => setWhenOption('week')}
                    style={{ flex: 1, minWidth: '45%' }}
                  >
                    <YStack
                      paddingVertical="$3"
                      borderRadius={12}
                      borderWidth={1}
                      borderColor={
                        whenOption === 'week'
                          ? COLORS.PRIMARY
                          : isDark
                          ? 'rgba(255, 255, 255, 0.2)'
                          : 'rgba(0, 0, 0, 0.2)'
                      }
                      backgroundColor={
                        whenOption === 'week' ? COLORS.PRIMARY : 'transparent'
                      }
                      alignItems="center"
                    >
                      <Text
                        fontSize={14}
                        fontWeight="500"
                        color={whenOption === 'week' ? COLORS.WHITE : textColor}
                      >
                        Ten tydzień
                      </Text>
                    </YStack>
                  </Pressable>

                  <Pressable
                    onPress={() => setWhenOption('any')}
                    style={{ flex: 1, minWidth: '45%' }}
                  >
                    <YStack
                      paddingVertical="$3"
                      borderRadius={12}
                      borderWidth={1}
                      borderColor={
                        whenOption === 'any'
                          ? COLORS.PRIMARY
                          : isDark
                          ? 'rgba(255, 255, 255, 0.2)'
                          : 'rgba(0, 0, 0, 0.2)'
                      }
                      backgroundColor={
                        whenOption === 'any' ? COLORS.PRIMARY : 'transparent'
                      }
                      alignItems="center"
                    >
                      <Text
                        fontSize={14}
                        fontWeight="500"
                        color={whenOption === 'any' ? COLORS.WHITE : textColor}
                      >
                        Dowolny
                      </Text>
                    </YStack>
                  </Pressable>
                </XStack>
              </YStack>
            </YStack>
          </ScrollView>

          {/* Bottom Footer with Apply Button */}
          <YStack
            padding="$4"
            gap="$3"
            borderTopWidth={1}
            borderTopColor={
              isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
            }
            backgroundColor={bgColor}
          >
            <YStack alignItems="center">
              <Text fontSize={18} fontWeight="700" color={textColor}>
                {announcementCount} ogłoszeń
              </Text>
              <Text fontSize={12} color={subtextColor}>
                odpowiada Twoim kryteriom
              </Text>
            </YStack>

            <CustomButton
              onPress={handleApplyFilters}
              color={COLORS.WHITE}
              backgroundColor={COLORS.PRIMARY}
              paddingHorizontal="$4"
              paddingVertical="$3.5"
              borderRadius={12}
            >
              <Text fontSize={16} fontWeight="700" color={COLORS.WHITE}>
                Zastosuj filtry
              </Text>
            </CustomButton>
          </YStack>
        </Sheet.Frame>
      </Sheet>
    );
  },
);

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  slider: {
    width: '100%',
    height: 40,
  },
});

FilterSheet.displayName = 'FilterSheet';

export default FilterSheet;
